import { Injectable } from '@angular/core';
import { HttpService } from '../http/http.service';
import { stockIssued } from '../../interface/reports/stockissued';
import { ledgerDate } from '../../interface/ledger/ledger';
import { firstValueFrom } from 'rxjs';
import { SalesReport } from '../../interface/reports/sales-report';
import { PurchaseReport } from '../../interface/purchase-report/purchase-report';
import { DashboardStats, ExpenseReport, SalesSummaryReport } from '../../interface';
import { SalesByGroupReport } from '../../interface/reports/sales-by-group-report.interface';
import { SalesByProductGroupReport } from '../../interface/reports/sales-by-product-group-report.interface';
import { InventoryAgingItem } from '../../interface/reports/inventory-aging.interface';
import { StockTurnoverReport } from '../../interface/reports/stock-turnover.interface';
import { CashFlowReport } from '../../interface/reports/cash-flow.interface';
import { ExpenseByHeadReport } from '../../interface/reports/expense-by-head.interface';
import { DailySummary } from '../../interface/daily-summary/daily-summary';
import { DailyServiceReport } from '../../interface/reports/daily-service-report.interface';
import { toDateParam } from 'src/app/utils';

@Injectable({
  providedIn: 'root'
})
export class ReportService {

  constructor(private _httpService: HttpService) { }
  getAll(data: stockIssued) {
    return firstValueFrom(this._httpService.get('/Reports/stocks/issued?startInvoiceId=' + data.startInvoiceId + '&endInvoiceId=' + data.endInvoiceId));
  }

  public async getDashboardStats(numberOfDays: number = 1): Promise<DashboardStats> {
    return await firstValueFrom(this._httpService.get('/Reports/dashboard?days=' + numberOfDays + '&endDate=' + toDateParam(new Date())));
  }
  public async getSummary(numberOfDays: number = 1): Promise<any> {
    return await firstValueFrom(this._httpService.get('/Reports/dailySummary?days=' + numberOfDays + '&endDate=' + toDateParam(new Date())));
  }
  async getBusinessUpdate(): Promise<any> {
    return await firstValueFrom(this._httpService.get('/Reports/BusinessUpdate'));

  }
  getProfitNLoss(data: ledgerDate) {
    return firstValueFrom(this._httpService.get(`/Reports/ProfitAndLoss?${this._buildDateParams(data.startDate, data.endDate)}`));
  }
  getTrend(days: number = 30): Promise<any> {
    return firstValueFrom(this._httpService.get(`/Reports/trend?days=${days}`));
  }
  getSalesReport(data: SalesReport): Promise<SalesSummaryReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales?${this._buildDateParams(data.startDate, data.endDate)}`));
  }
  getPurchaseReport(data: PurchaseReport): Promise<any> {
    return firstValueFrom(this._httpService.get(`/Reports/Purchase?${this._buildDateParams(data.startDate, data.endDate)}`));
  }
  getExpenseReport(data: PurchaseReport): Promise<ExpenseReport> {
    return firstValueFrom(this._httpService.get(`/Reports/expenseSummary?${this._buildDateParams(data.startDate, data.endDate)}`));
  }
  getExpenseByDateReport(data: PurchaseReport): Promise<ExpenseReport> {
    return firstValueFrom(this._httpService.get(`/Reports/ExpenseByDate?${this._buildDateParams(data.startDate, data.endDate)}`));
  }

  /**
   * Range bounds are calendar days in the caller's timezone. The server widens them to UTC
   * itself using the `X-Timezone` header, so they go over the wire as bare dates — converting
   * to an instant here would have the day truncated a second time server-side.
   */
  private _buildDateParams(startDate: string | Date, endDate: string | Date): string {
    return `startDate=${toDateParam(startDate)}&endDate=${toDateParam(endDate)}`;
  }

  getSalesByCustomer(startDate: Date, endDate: Date): Promise<SalesByGroupReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales/ByCustomer?${this._buildDateParams(startDate, endDate)}`));
  }

  getSalesByTown(startDate: Date, endDate: Date): Promise<SalesByGroupReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales/ByTown?${this._buildDateParams(startDate, endDate)}`));
  }

  getSalesByRoute(startDate: Date, endDate: Date): Promise<SalesByGroupReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales/ByRoute?${this._buildDateParams(startDate, endDate)}`));
  }

  getSalesByProductPerCustomer(startDate: Date, endDate: Date): Promise<SalesByProductGroupReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales/ProductByCustomer?${this._buildDateParams(startDate, endDate)}`));
  }

  getSalesByProductPerTown(startDate: Date, endDate: Date): Promise<SalesByProductGroupReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales/ProductByTown?${this._buildDateParams(startDate, endDate)}`));
  }

  getSalesByProductPerRoute(startDate: Date, endDate: Date): Promise<SalesByProductGroupReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales/ProductByRoute?${this._buildDateParams(startDate, endDate)}`));
  }

  getCustomerGrowth(startDate: Date, endDate: Date, routeId?: number, townId?: number): Promise<any> {
    let params = this._buildDateParams(startDate, endDate);
    if (routeId != null) params += `&routeId=${routeId}`;
    if (townId  != null) params += `&townId=${townId}`;
    return firstValueFrom(this._httpService.get(`/Reports/Customers/Growth?${params}`));
  }

  getInventoryAging(): Promise<InventoryAgingItem[]> {
    return firstValueFrom(this._httpService.get('/Reports/Stocks/Aging'));
  }

  getStockTurnover(startDate: Date, endDate: Date): Promise<StockTurnoverReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Stocks/Turnover?${this._buildDateParams(startDate, endDate)}`));
  }

  getCashFlowSummary(startDate: Date, endDate: Date): Promise<CashFlowReport> {
    return firstValueFrom(this._httpService.get(`/Reports/CashFlow?${this._buildDateParams(startDate, endDate)}`));
  }

  getExpenseByHead(startDate: Date, endDate: Date): Promise<ExpenseByHeadReport> {
    return firstValueFrom(this._httpService.get(`/Reports/ExpenseByHead?${this._buildDateParams(startDate, endDate)}`));
  }

  getDailyServiceReport(startDate: Date, endDate: Date): Promise<DailyServiceReport> {
    return firstValueFrom(this._httpService.get(`/Reports/Sales/ServiceDaily?${this._buildDateParams(startDate, endDate)}`));
  }

  getDailyReport(date: Date): Promise<DailySummary> {
    return firstValueFrom(this._httpService.get(`/Reports/dailySummary?endDate=${toDateParam(date)}&days=1`));
  }

  // ── PDF print helpers ──────────────────────────────────────────────────────

  private async _openPdf(url: string): Promise<void> {
    const blob = await firstValueFrom(this._httpService.getBlob(url));
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
  }

  printProfitAndLoss(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/ProfitAndLoss/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printSalesReport(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printPurchaseReport(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Purchase/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printExpenseSummary(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/ExpenseSummary/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printExpenseByDate(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/ExpenseByDate/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printExpenseByHead(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/ExpenseByHead/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printSalesByCustomer(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/ByCustomer/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printSalesByTown(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/ByTown/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printSalesByRoute(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/ByRoute/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printSalesByProductPerCustomer(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/ProductByCustomer/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printSalesByProductPerTown(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/ProductByTown/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printSalesByProductPerRoute(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/ProductByRoute/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printCustomerGrowth(startDate: Date, endDate: Date, routeId?: number, townId?: number): Promise<void> {
    let params = this._buildDateParams(startDate, endDate);
    if (routeId != null) params += `&routeId=${routeId}`;
    if (townId != null) params += `&townId=${townId}`;
    return this._openPdf(`/Reports/Customers/Growth/pdf?${params}`);
  }

  printInventoryAging(): Promise<void> {
    return this._openPdf('/Reports/Stocks/Aging/pdf');
  }

  printStockTurnover(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Stocks/Turnover/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printCashFlow(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/CashFlow/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  printDailyServiceReport(startDate: Date, endDate: Date): Promise<void> {
    return this._openPdf(`/Reports/Sales/ServiceDaily/pdf?${this._buildDateParams(startDate, endDate)}`);
  }

  async downloadDailyServiceReportCsv(startDate: Date, endDate: Date): Promise<void> {
    const blob = await firstValueFrom(this._httpService.getBlob(`/Reports/Sales/ServiceDaily/csv?${this._buildDateParams(startDate, endDate)}`));
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'daily-sale-report-to-submit.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }

  printDailySummary(date: Date): Promise<void> {
    return this._openPdf(`/Reports/dailySummary/pdf?endDate=${toDateParam(date)}`);
  }
}
